import { useEffect, useState } from "react";
import { Pause, Play, Pencil, Trash2, MapPin, Truck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DispatcherWatch, useDispatcherWatches } from "@/hooks/useDispatcherWatches";
import { WatchLaneModal } from "./WatchLaneModal";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  onViewAlerts?: (watchId: string) => void;
}

export function WatchesList({ onViewAlerts }: Props) {
  const { watches, loading, toggleStatus, archiveWatch } = useDispatcherWatches();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [editing, setEditing] = useState<DispatcherWatch | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [unreadByWatch, setUnreadByWatch] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("notifications")
        .select("metadata")
        .eq("recipient_user_id", userId)
        .eq("type", "dispatcher_match")
        .eq("is_read", false)
        .limit(500);
      if (cancelled) return;
      const counts: Record<string, number> = {};
      for (const row of (data as any[]) || []) {
        const wid = row?.metadata?.watch_id;
        if (!wid) continue;
        counts[wid] = (counts[wid] || 0) + 1;
      }
      setUnreadByWatch(counts);
    })();
    return () => { cancelled = true; };
  }, [userId, watches.length]);

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading watches…</div>;
  if (!watches.length) {
    return (
      <div className="text-sm text-muted-foreground py-16 text-center border border-dashed rounded-lg">
        No watches yet. Click <strong>Watch a Lane</strong> to have ATLAS monitor for matching loads.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {watches.map((w) => {
        const unread = unreadByWatch[w.id] || 0;
        return (
          <div key={w.id} className="border rounded-lg p-3 bg-card flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <strong className="text-sm">{w.label || "Untitled watch"}</strong>
                <Badge variant={w.status === "active" ? "default" : "secondary"} className={w.status === "active" ? "bg-brand-teal" : ""}>
                  {w.status}
                </Badge>
                {w.auto_score && <Badge variant="outline" className="text-[10px]">auto-score</Badge>}
              </div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{w.current_city || "?"}, {w.current_state || "?"} · {w.max_deadhead_miles}mi</span>
                <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" />{w.equipment_types.join(", ") || "—"}</span>
                {w.min_rate && <span>≥ ${w.min_rate}</span>}
                {w.min_rpm && <span>≥ ${w.min_rpm}/mi</span>}
                {w.destination_states?.length ? <span>→ {w.destination_states.join(", ")}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs gap-1"
                onClick={() => onViewAlerts?.(w.id)}
                disabled={!onViewAlerts}
                title="View alerts for this watch"
              >
                <Bell className="h-3 w-3" />
                Alerts
                {unread > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand-teal text-white text-[10px] font-semibold">
                    {unread}
                  </span>
                )}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => toggleStatus(w.id, w.status).then(() => toast.success(w.status === "active" ? "Paused" : "Resumed"))}>
                {w.status === "active" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => { setEditing(w); setEditOpen(true); }}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(w.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        );
      })}

      <WatchLaneModal open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditing(null); }} editing={editing} />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this watch?</AlertDialogTitle>
            <AlertDialogDescription>ATLAS will stop matching for this lane. Existing matches in your inbox stay.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!confirmDelete) return;
                await archiveWatch(confirmDelete);
                toast.success("Watch archived");
                setConfirmDelete(null);
              }}
            >Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
